import { useCallback, useRef, useState } from "react";

/**
 * Reusable async action helper for template flows.
 * Supports loading state, optional AbortController, per-item loading key, and blocking concurrent clicks.
 */
export default function useTemplateAsyncAction() {
  const [loading, setLoading] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const abortControllerRef = useRef(null);
  const inFlightRef = useRef(false);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    inFlightRef.current = false;
    setLoading(false);
    setLoadingKey(null);
  }, []);

  const run = useCallback(async (
    asyncFn,
    { useAbort = false, preventConcurrent = true, key = null } = {},
  ) => {
    if (preventConcurrent && inFlightRef.current) {
      return undefined;
    }

    if (useAbort && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = useAbort ? new AbortController() : null;
    if (controller) {
      abortControllerRef.current = controller;
    }

    inFlightRef.current = true;
    setLoading(true);
    if (key != null) {
      setLoadingKey(key);
    }

    try {
      const result = await asyncFn(controller?.signal);
      if (controller?.signal.aborted) {
        return undefined;
      }
      return result;
    } finally {
      inFlightRef.current = false;
      if (!controller || abortControllerRef.current === controller) {
        abortControllerRef.current = null;
        setLoading(false);
        setLoadingKey(null);
      }
    }
  }, []);

  return {
    loading,
    loadingKey,
    run,
    abort,
  };
};
